/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import { Grid } from '@coursera/cds-core';

import EnterpriseOrgScopedSearchBarGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseOrgScopedSearchBarGridItem';
import ExploreMegaMenuGridItem from 'bundles/composable-page-header-enterprise/components/ExploreMegaMenuGridItem';
import type { UseEnterpriseMyPageHeaderResult as MyEnterprisePageHeader } from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import {
  getEnterpriseOrgLogo,
  getEnterpriseOrgLogoLabel,
  getLinkForOrgHome,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import HelpDropdownGridItem from 'bundles/composable-page-header/components/HelpDropdownGridItem';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import LanguageDropdownLegacyGridItem from 'bundles/composable-page-header/components/grid-items/LanguageDropdownLegacyGridItem';
import LeftNavButtonGridItem from 'bundles/composable-page-header/components/grid-items/LeftNavButtonGridItem';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import CoachStudioGridItem, {
  showCoachStudio,
} from 'bundles/composable-page-header/components/grid-items/coach/CoachStudioGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import DefaultAccountDropdownGridItemRenderer from 'bundles/composable-page-header/module-renderers/DefaultAccountDropdownGridItemRenderer';
import { isOnMyLearningPage } from 'bundles/composable-page-header/utils/headerHighlightLinkUtils';

import _t from 'i18n!nls/composable-page-header-enterprise';

const styles = {
  leftSide: css`
    width: auto;
  `,
  middleSide: css`
    flex-shrink: 1;
    flex-grow: 1;
    flex-basis: 0%;
    padding: 0 8px;
    width: 100%;
    min-width: 0;
  `,
  rightSide: css`
    flex-shrink: 1;
    flex-grow: 1;
    flex-basis: 0%;
    width: auto;
  `,
  exploreItem: css`
    display: flex;
    flex-direction: row;
  `,
};

type Props = {
  pageHeaderData: MyEnterprisePageHeader;
  hideSearch?: boolean;
  hideExplore?: boolean;
};

const LoggedInOrgScopedEnterpriseDesktopMainNav = ({ pageHeaderData, hideSearch, hideExplore }: Props) => {
  const {
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    isCourseraPlusLearner,
    courseraPlusSubscriptionCycle,
    enrolledEnterprisePrograms,
    naptimeCurrentPageOrganization,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
    enrolledDegreePrograms,
    currentPageProgram,
  } = pageHeaderData;
  const location = useLocation();
  const currentOrganization = naptimeCurrentPageOrganization ?? naptimeChosenOrganization;
  const orgBasePath = `/organizations/${currentOrganization?.slug}`;
  const myLearningUrl = `${orgBasePath}/my-learning?myLearningTab=IN_PROGRESS`;
  // Only the enterprise-org-home app owns the org home and my-learning routes. Sibling org-scoped
  // paths (e.g. /career-academy) are separate apps whose routers would render NotFound on a
  // client-side push, so those keep a full page load.
  const canRouteMyLearningClientSide =
    !!currentOrganization && (location.pathname === orgBasePath || location.pathname === `${orgBasePath}/my-learning`);

  return (
    <MainNavigationWrapper>
      <Grid item container alignItems="center" spacing={8} css={styles.leftSide}>
        <Grid item>
          <HomeLogoGridItemModule
            href={currentOrganization ? getLinkForOrgHome(currentOrganization) : '/'}
            logos={currentOrganization ? [getEnterpriseOrgLogo(currentOrganization)] : [<CourseraHomeLogo key="1" />]}
            ariaLabel={currentOrganization ? getEnterpriseOrgLogoLabel(currentOrganization) : undefined}
          />
        </Grid>
        {!hideExplore && (
          <Grid item css={styles.exploreItem}>
            <ExploreMegaMenuGridItem />
          </Grid>
        )}
        {currentOrganization &&
          naptimeOrganizationsWithProgramEnrollments?.some(
            (organization) => organization.id === currentOrganization.id
          ) && (
            <Grid item>
              <LeftNavButtonGridItem
                label={_t('My Learning')}
                href={myLearningUrl}
                name="my_learning"
                trackingName="lihp_my_learning"
                isCurrent={isOnMyLearningPage(location.pathname)}
                enableClientSideNavigation={canRouteMyLearningClientSide}
              />
            </Grid>
          )}
      </Grid>
      <Grid item container alignItems="center" css={styles.middleSide}>
        {!hideSearch && (
          <EnterpriseOrgScopedSearchBarGridItem
            thirdPartyOrganizationSlug={
              naptimeCurrentPageOrganization?.slug ?? currentPageProgram?.naptimeThirdPartyOrganization?.slug
            }
            enrolledEnterprisePrograms={enrolledEnterprisePrograms}
            chosenProgramSlug={currentPageProgram?.slug}
            chosenProgramId={currentPageProgram?.id}
          />
        )}
      </Grid>
      <Grid item container alignItems="center" justifyContent="flex-end" wrap="nowrap" css={styles.rightSide}>
        <Grid item>
          <HelpDropdownGridItem disableChatSsr={true} />
        </Grid>
        <LanguageDropdownLegacyGridItem currentThirdPartyOrgId={naptimeChosenOrganization?.id} />
        <Grid item>
          <NotificationsLegacyGridItem />
        </Grid>
        {showCoachStudio() && (
          <Grid item>
            <CoachStudioGridItem />
          </Grid>
        )}
        <Grid item>
          <DefaultAccountDropdownGridItemRenderer
            hasEnrolledPrograms={!!naptimeOrganizationsWithProgramEnrollments || !!enrolledDegreePrograms}
            naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
            hasCheatingIncidents={!!hasCheatingIncidents}
            canViewEducatorAdminLink={!!canViewEducatorAdminLink}
            isCourseraPlusLearner={!!isCourseraPlusLearner}
            courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
            currentOrganization={currentOrganization}
            organizations={naptimeOrganizationsWithProgramEnrollments}
            degrees={enrolledDegreePrograms}
          />
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default LoggedInOrgScopedEnterpriseDesktopMainNav;
